// Decides *when* to ask the browser for a service-worker update check.
//
// Pure core: no React, no Workbox, no Sentry. Every collaborator — the trigger
// sources, the clock, the check itself, and failure reporting — is injected, so
// this module can be exercised entirely with fake timers.
//
// The trigger set is the open axis of change (the incident this exists to fix
// was a *missing* trigger), so sources are a uniform list rather than a
// named-method interface: adding one costs a file and a composition line, and
// leaves the scheduler untouched.

/** A trigger source: call `fire` when it wants a check; return an unsubscribe. */
export type UpdateSignal = (fire: () => void) => () => void;

export interface UpdateTriggerOptions {
	signals: readonly UpdateSignal[];
	checkForUpdate: () => Promise<void>;
	recordCheckFailure: (error: ServiceWorkerUpdateCheckError) => void;
	now: () => number;
	minIntervalMs?: number;
}

export class ServiceWorkerUpdateCheckError extends Error {
	constructor(cause: unknown) {
		super("service-worker update check failed", { cause });
		// Named so Sentry groups it as a known failure mode rather than as a
		// generic `Error`. A failed check is expected on a rural link.
		this.name = "ServiceWorkerUpdateCheckError";
	}
}

/**
 * The single rate-limit floor shared by every signal. It is what bounds the
 * network budget at <= 12 conditional `GET /sw.js` per hour per document — a
 * fifth signal cannot change that number because the floor is enforced here
 * and not per call site.
 */
const DEFAULT_MIN_INTERVAL_MS = 5 * 60_000;

export function startUpdateTriggers(options: UpdateTriggerOptions): () => void {
	const {
		signals,
		checkForUpdate,
		recordCheckFailure,
		now,
		minIntervalMs = DEFAULT_MIN_INTERVAL_MS,
	} = options;

	let lastCheckAt = Number.NEGATIVE_INFINITY;
	let inFlight = false;
	let disposed = false;

	async function runCheck(): Promise<void> {
		try {
			await checkForUpdate();
		} catch (error) {
			recordCheckFailure(new ServiceWorkerUpdateCheckError(error));
		} finally {
			inFlight = false;
		}
	}

	function fire(): void {
		// Read-and-set both guards before any `await`. Single-threaded JS makes
		// check-then-act atomic here, so two signals firing in the same tick
		// collapse to one check without inventing a lock.
		if (disposed || inFlight) return;
		const at = now();
		if (at - lastCheckAt < minIntervalMs) return;
		lastCheckAt = at;
		inFlight = true;
		void runCheck();
	}

	const unsubscribes = signals.map((signal) => signal(fire));

	return () => {
		if (disposed) return;
		disposed = true;
		for (const unsubscribe of unsubscribes) unsubscribe();
	};
}
